import { useContext, useState } from 'react';
import { Box, Button, IconButton, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import axios from 'axios';
import IResponse from '../../../interfaces/response';
import { UserContext } from '../../../contexts/UserContext';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

const newPasswordSchema = z
	.object({
		password: z.string().min(6, 'invalid_password_message'),
		repeatPassword: z.string().min(6, 'invalid_password_message'),
	})
	.refine(data => data.password === data.repeatPassword, {
		path: ['repeatPassword'],
		message: 'passwords_do_not_match_message',
	});

const NewPassword = () => {
	const [isDisabled, _] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showRepeatPassword, setShowRepeatPassword] = useState(false);

	const { t } = useTranslation();
	const navigate = useNavigate();

	const { recoverEmailOrPhone } = useContext(UserContext);

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<z.infer<typeof newPasswordSchema>>({
		resolver: zodResolver(newPasswordSchema),
	});

	const toggleShowPassword = () => {
		setShowPassword(old => !old);
	};

	const toggleShowRepeatPassword = () => {
		setShowRepeatPassword(old => !old);
	};

	const handleSetNewPassword = (data: z.infer<typeof newPasswordSchema>) => {
		axios<IResponse<string>>({
			url: '/users/forgot-pass/reset-pass',
			method: 'POST',
			data: {
				login: recoverEmailOrPhone,
				password: data.password,
			},
		}).then(res => {
			if (res.data.success) {
				navigate('/auth/login');
			}
		});
	};

	return (
		<Box>
			<form className="flex w-full flex-col gap-y-6" onSubmit={handleSubmit(handleSetNewPassword)}>
				<TextField
					InputProps={{
						endAdornment: (
							<IconButton onClick={toggleShowPassword} className="text-text-secondary">
								{showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
							</IconButton>
						),
					}}
					className="w-full"
					id="outlined-password-input"
					label={t('new-password')}
					type={showPassword ? 'text' : 'password'}
					autoComplete="off"
					{...register('password')}
					error={!!errors.password}
					helperText={t(errors.password?.message!)}
				/>
				<TextField
					InputProps={{
						endAdornment: (
							<IconButton onClick={toggleShowRepeatPassword} className="text-text-secondary">
								{showRepeatPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
							</IconButton>
						),
					}}
					className="w-full"
					id="outlined-password-input"
					label={t('repeat-new-password')}
					type={showPassword ? 'text' : 'password'}
					autoComplete="off"
					{...register('repeatPassword')}
					error={!!errors.repeatPassword}
					helperText={t(errors.repeatPassword?.message!)}
				/>
				<Button type="submit" size="large" variant="contained" disabled={isDisabled} className="text-base font-normal">
					{t('confirm')}
				</Button>
			</form>
		</Box>
	);
};

export default NewPassword;
